/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {loadModules} from 'esri-loader';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {FloatingIconButtonGroup} from '@ion/dls/src/components/buttons/FloatingIconButton';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import {CloseFullScreenIcon} from '@ion/dls/src/icons/CloseFullScreenIcon';
import {OpenFullScreenIcon} from '@ion/dls/src/icons/OpenFullScreenIcon';
import {IconSize, MarkerProps} from '@ion/dls/src/types/types';

type FullScreenToolProps = Omit<MarkerProps, 'color'>;

// defines the prop definition for FullScreenToolFactory component...
export interface FullScreenToolFactoryProps {
  view: __esri.MapView;
};

// defines the different state of full screen tool...
export enum State {
  active = 'active',
  ready = 'ready',
  unsupported = 'feature-unsupported',
  disabled= 'disabled'
};

/**
 * The FullScreenTool provides the UI for FullScreenTool icon that can be placed on the
 * map layers.
 *
 * ```typescript
 *
 *   <FullScreenTool size={IconSize.medium} onClick={function() {}} />
 *
 * ```
 */
export const FullScreenTool = ({size, disabled, isSelected, onClick}: FullScreenToolProps) => (
  <FloatingIconButtonGroup>
    <FloatingIconButtonGroup.FloatingIconButton onClick={onClick} disabled={disabled}>
      {!isSelected ? (
        <OpenFullScreenIcon size={size} />
      ) : (
        <CloseFullScreenIcon size={size} />
      )}
    </FloatingIconButtonGroup.FloatingIconButton>
  </FloatingIconButtonGroup>
);

/**
 * The FullScreenToolFactory funtion is responsible for creating a FullScreen Tool/widget
 * component that provide an option to enter/exit full screen. This is a stand-alone
 * tool, can be usable in any of position on the map.
 *
 * ```typescript
 *
 *  import FullScreenToolFactory from '../tools/FullScreenTool';
 *
 *  const component = FullScreenToolFactory({view});
 *
 * ```
 */
export const FullScreenToolFactory = ({view}: FullScreenToolFactoryProps) => {
  const fullScreenRef               = useRef(null);
  const [disabled, setDisabled]     = useState(false);
  const [fullScreen, setFullScreen] = useState(false);

  const translations = useMemo(() => translate('maps:controls.fullscreen', {returnObjects: true}), []);

  const title = useMemo(() => {
    let title = translations.enter;
    if (disabled) {
      title = translations.noSupport;
    } else if (fullScreen) {
      title = translations.exit;
    }
    return title;
  }, [fullScreen, disabled, translations]);

  useEffect(() => {
    loadModules(['esri/widgets/Fullscreen'])
      .then(([Fullscreen]) => {
        fullScreenRef.current = new Fullscreen({view});
        const {state} = fullScreenRef.current?.viewModel || {};
        setDisabled(!state || state === State.unsupported || state === State.disabled);
      })
      .catch((err) => {
        setDisabled(true);
        error(err);
      });
  }, []);

  const onClick = useCallback(() => {
    fullScreenRef.current?.viewModel?.toggle();
    setFullScreen(!fullScreen);
  }, [fullScreen]);

  return (
    <Tooltip title={title}>
      <div>
        <FullScreenTool isSelected={fullScreen}
          disabled={disabled}
          size={IconSize.medium}
          onClick={onClick} />
      </div>
    </Tooltip>
  );
};

export default memo(FullScreenToolFactory);
